"use client";

import { useEffect, useMemo, useState } from "react";
import { CompetitionData } from "@/app/components/ActiveCompetitions/types";
import { useActiveCompetitions } from "@/app/redux/features/categories/selectors";
import { XC_NEW_SLUGS } from "./xcNewSlugs";

const EMPTY_COMPETITION = {
  AdditionalCashText: "",
  CompetitionId: 0,
  CompetitionPictureTypeId: 0,
  CompetitionTypePrefix: "",
  CtaAddToBasketPrizeId: 0,
  CtaText: "",
  CtaTextValidated: "",
  CtaTicketValue: "",
  CurrencySymbol: "",
  EndDate: "",
  EndDateTime: "",
  FeaturedPrizeId: 0,
  FeaturedPrizeModel: "",
  FeaturedPrizeThumbnail: "",
  FormattedOriginalPrice: "",
  FormattedPrice: "",
  FormattedPriceWithoutCurrency: "",
  HasInstantWin: false,
  ImageDoubleUrl: "",
  ImageUrl: "",
  IsMobileAppOnly: false,
  IsMobileOnlyCompetition: false,
  IsMultibuyEnabled: false,
  IsRewardCompetition: false,
  IsSinglePrizeMode: false,
  MobileTitle: "",
  MultiBuyLowerPercent: 0,
  MultiBuyLowerQuantity: 0,
  MultiBuyUpperPercent: 0,
  MultiBuyUpperQuantity: 0,
  NavigationColour: "",
  OverrideCompetitionTypeId: undefined,
  OverrideDiscountValue: 0,
  PageUrl: "",
  PercentOffValue: 0,
  PostalEntryText: "",
  SubtitleText: "",
  SubtitleTextValidated: "",
  SupportsMultibuy: false,
  TicketText: "",
  Title: "",
  UseDailyPopupForCompetitionId: 0,
} as CompetitionData;

/**
 * Canonical prize path for CompetitionPageUrl matching.
 * `/prizes/winbig-new` → `/prizes/winbig`
 * `/prizes/3winbig-new` → `/prizes/3winbig`
 * `/prizes/car-v2` stays as-is (dev/fallback route)
 */
export const toCanonicalPrizePath = (pathname: string) => {
  if (!pathname) return "";
  const [pathOnly] = pathname.split(/[?#]/);
  const segments = pathOnly.split("/").filter(Boolean);
  if (segments.length >= 2 && segments[1].toLowerCase().endsWith("-new")) {
    segments[1] = segments[1].slice(0, -4);
  }
  return `/${segments.join("/")}`;
};

const pickXcBySlug = (competitions: CompetitionData[], canonicalPath: string) => {
  if (!canonicalPath) return null;

  const xcCompetitions = competitions.filter(c => c.CompetitionTypePrefix === "XC" && c.PageUrl);
  const path = canonicalPath.toLowerCase();

  const exact = xcCompetitions.find(c => c.PageUrl.toLowerCase() === path);
  if (exact) return exact;

  // Same StartsWith rule as BotBWebContext — longest PageUrl wins
  const byPrefix = xcCompetitions
    .filter(c => path.startsWith(c.PageUrl.toLowerCase()))
    .sort((a, b) => b.PageUrl.length - a.PageUrl.length);

  return byPrefix[0] ?? null;
};

/**
 * Resolve the XC competition for the current prize page from the URL slug.
 * Prefer PageUrl match after stripping a trailing `-new` segment.
 * On `*-new` paths with no XC match → empty (never bind HC / wrong comps).
 * Fallback to first XC only on non-`-new` routes (e.g. local `/prizes/car-v2`).
 */
export default function useXcCompetition() {
  const activeCompetitions = useActiveCompetitions();
  const [pathname, setPathname] = useState("");

  useEffect(() => {
    setPathname(window.location.pathname);
  }, []);

  return useMemo(() => {
    const canonicalPath = toCanonicalPrizePath(pathname);
    const matched = pickXcBySlug(activeCompetitions, canonicalPath);
    if (matched) return matched;

    const slug = pathname.split("/").filter(Boolean)[1]?.toLowerCase() ?? "";
    const isXcNewPreviewPath = XC_NEW_SLUGS.has(slug);
    if (isXcNewPreviewPath) {
      return EMPTY_COMPETITION;
    }

    return activeCompetitions.find(c => c.CompetitionTypePrefix === "XC") ?? EMPTY_COMPETITION;
  }, [activeCompetitions, pathname]);
}
